import React from "react";
import { isEmpty } from "lodash";
import { PreviewLayout } from "./";
import { Lazy } from "@/components/Lazy";
import { checkValidURL } from "@/utils/formHelper";
import { Link } from "../Link";
import { IconList } from "../IconList";
import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";
import { QREditorContext } from "@/contexts/QREditorContext";
import { getFullAddress } from "@/utils/qrEditorHelper";
import { Typography } from "../Typography";
import { formatToBusinessHoursPreview } from "@/utils/timerHelper";
import { BusinessHourTime } from "../BusinessHours/types";
import { Icons } from "@/types/icons";
import { PreviewLocationBlock } from "../PreviewLocationBlock";
import { LocationType } from "../PreviewLocationBlock/types";
import { optimizeCloudinaryImage } from "@/services/CloudinaryService";

export default function PreviewBusiness(props: QRProps<"business">) {
  const t = props.previewTranslations;

  const qrEditorContextData = React.useContext<QREditorContextProps<"business">>(QREditorContext);
  const data = props?.data || qrEditorContextData?.qrData.data;

  const isInitPreview = props?.initPreview;

  const { address, city, businessHours, country, numeration, state, zipCode } = data;

  const fullAddress = getFullAddress(address, numeration, city, zipCode, state, country);
  const addressData: LocationType = {
    address,
    city,
    country,
    numeration,
    state,
    zipCode,
    fullAddress,
  };

  const isDayEnabled = () => {
    if (!businessHours) return false;

    const enabledDays = Object.values(businessHours.days).filter((day) => day?.enabled);
    return enabledDays.length > 0;
  };
  const renderDay = (day: string, translation: string) => {
    return (
      <div className="business-opening-hours-container">
        <Typography
          Tag="p"
          size="xs"
          classname="u-spacing-0 u-grey-800">
          {translation}
        </Typography>
        <div className="business-opening-hours-list">
          {data?.businessHours?.days[day]?.times.map((time: BusinessHourTime, idx: number) => {
            return (
              <Typography
                key={`${day}-${idx}`}
                Tag="p"
                size="xs"
                classname="u-spacing-0 u-grey">
                {formatToBusinessHoursPreview(time.start, data?.businessHours.format)}
                {" - "}
                {formatToBusinessHoursPreview(time.end, data?.businessHours.format)}
              </Typography>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <PreviewLayout
      page="business"
      dataQA="preview-business-container"
      backgroundColor={data?.themeColor?.backgroundColor}
      includeBrowser
      isInitPreview={isInitPreview}>
      <div className="business-name">
        {data?.companyName && (
          <Typography
            Tag="p"
            classname="u-semibold">
            {data?.companyName}
          </Typography>
        )}
      </div>

      <div style={{ position: "relative" }}>
        {data?.logoUrl && (
          <>
            <div className="background-divider-top" />
            <div className="background-divider-bottom" />
            <div className="business-logo--container">
              <Lazy placeholderClass="business-logo--background">
                <img
                  src={optimizeCloudinaryImage({
                    url: data?.logoUrl,
                    transformations: ["f_auto,q_auto,w_1280,h_640,dpr_auto,c_fill"],
                  })}
                  className="business-logo--background"
                />
              </Lazy>
            </div>
          </>
        )}
        {data?.title && (
          <div className={`business-title  ${data?.logoUrl ? "hasLogo" : ""} `}>
            <Typography
              Tag="p"
              size="l"
              classname="u-semibold u-spacing-4">
              {data?.title}
            </Typography>
          </div>
        )}
      </div>

      <div className="qr-content">
        {data?.subtitle && (
          <div className="business-subtitle ">
            <Typography
              Tag="p"
              classname="u-spacing-3">
              {data?.subtitle}
            </Typography>
          </div>
        )}

        {data?.buttonText && (
          <Link
            className="button u-spacing-2 business-button"
            href={checkValidURL(data?.buttonUrl) || ""}
            style={{ backgroundColor: data?.themeColor?.buttonColor, alingContent: "center" }}
            externalLink
            type="button"
            prefetch={false}>
            <span
              data-testid="button-label"
              className="button__text">
              {data?.buttonText}
            </span>
          </Link>
        )}

        {isDayEnabled() && (
          <div className="u-card">
            <IconList
              iconItem
              items={{
                icon: Icons.clock,
                title: isInitPreview ? "Opening hours" : t && t.businessPreviewOpeningHours,
              }}
              tag={"h6"}
              semibold
            />
            {data?.businessHours.days.monday.enabled &&
              renderDay("monday", isInitPreview ? "Monday" : t?.businessPreviewOpeningHoursMonday)}
            {data?.businessHours.days.tuesday.enabled &&
              renderDay(
                "tuesday",
                isInitPreview ? "Tuesday" : t?.businessPreviewOpeningHoursTuesday
              )}
            {data?.businessHours.days.wednesday.enabled &&
              renderDay(
                "wednesday",
                isInitPreview ? "Wednesday" : t?.businessPreviewOpeningHoursWednesday
              )}
            {data?.businessHours.days.thursday.enabled &&
              renderDay("thursday", t?.businessPreviewOpeningHoursThursday)}
            {data?.businessHours.days.friday.enabled &&
              renderDay("friday", t?.businessPreviewOpeningHoursFriday)}
            {data?.businessHours.days.saturday.enabled &&
              renderDay("saturday", t?.businessPreviewOpeningHoursSaturday)}
            {data?.businessHours.days.sunday.enabled &&
              renderDay("sunday", t?.businessPreviewOpeningHoursSunday)}
          </div>
        )}

        <PreviewLocationBlock
          data={addressData}
          title={isInitPreview ? "Location" : t && t.businessPreviewLocation}
        />

        {!isEmpty(data?.facilities) && (
          <div className="u-card">
            <Typography
              Tag="p"
              size="m"
              classname="u-semibold">
              {t && t.businessPreviewFacilities}
            </Typography>
            <IconList
              compact
              items={data?.facilities}
            />
          </div>
        )}

        {(data?.contactName ||
          data?.contactPhone ||
          data?.contactEmail ||
          data?.contactWebsite) && (
          <div className="u-card">
            <IconList
              iconItem
              items={{
                icon: Icons.building,
                title: t && t.businessPreviewContactInformation,
              }}
              tag={"h6"}
              semibold
            />

            {data?.contactName && (
              <>
                <Typography
                  Tag="p"
                  size="xs"
                  classname="u-spacing-0 u-grey">
                  {t && t.businessPreviewName}
                </Typography>
                <Typography
                  Tag="p"
                  size="xs">
                  {data?.contactName}
                </Typography>
              </>
            )}
            {data?.contactPhone && (
              <>
                <Typography
                  Tag="p"
                  size="xs"
                  classname="u-spacing-0 u-grey">
                  {t && t.businessPreviewPhone}
                </Typography>
                <Typography
                  Tag="p"
                  size="xs">
                  <Link
                    externalLink
                    className="business-link"
                    href={`tel:${data?.contactPhone}`}
                    prefetch={false}>
                    {data?.contactPhone}
                  </Link>
                </Typography>
              </>
            )}
            {data?.contactEmail && (
              <>
                <Typography
                  Tag="p"
                  size="xs"
                  classname="u-spacing-0 u-grey">
                  {t && t.businessPreviewEmail}
                </Typography>
                <Typography
                  Tag="p"
                  size="xs">
                  <Link
                    externalLink
                    className="business-link"
                    href={`mailto:${data?.contactEmail}`}
                    prefetch={false}>
                    {data?.contactEmail}
                  </Link>
                </Typography>
              </>
            )}
            {data?.contactWebsite && (
              <>
                <Typography
                  Tag="p"
                  size="xs"
                  classname="u-spacing-0 u-grey">
                  {t && t.businessPreviewWebsite}
                </Typography>

                <Typography
                  Tag="p"
                  classname="u-spacing-0"
                  size="xs">
                  <Link
                    externalLink
                    className="business-link"
                    href={checkValidURL(data?.contactWebsite) || ""}
                    prefetch={false}>
                    {data?.contactWebsite}
                  </Link>
                </Typography>
              </>
            )}
          </div>
        )}

        {data?.aboutTheCompany && (
          <div className="u-card">
            <Typography
              Tag="p"
              size="m"
              classname="u-semibold">
              {t && t.businessPreviewAboutTheCompany}
            </Typography>
            <Typography
              Tag="p"
              classname="u-spacing-0 u-grey business-info">
              {data?.aboutTheCompany}
            </Typography>
          </div>
        )}
        {!isEmpty(data?.socialNetworks) && (
          <div className="business-socialNetworks">
            <IconList
              center
              items={data?.socialNetworks}
            />
          </div>
        )}
      </div>
    </PreviewLayout>
  );
}
